import React, { useState, useEffect } from 'react'
import { Modal, Form, Checkbox } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { BaseService } from 'utility/services/base'
import { UsersService } from 'utility/services/user'
import { APIPath } from 'utility/constant'
import { CustomerAssignModal } from './CustomerAssignModal'
import { TemplateAssignModal } from './TemplateAssignModal'
import { FileUploadPicker } from 'components/FileUploadPicker'
import { AcceptFileType, ShowPhotoGpsService } from 'utility'
import { InspectionService } from 'utility/services/inspection'
import { CustomePreview } from 'page-components/inspection/Elements'
import { X } from 'phosphor-react'
import { useNotification } from 'hooks/toast'
import { userUpdateErrorMessage } from 'utility/user-deactivate'

export const UserEditModal = ({ open, onClose, user, isProfileEdit = false, onUserUpdate }) => {
    const { successToast, errorToast } = useNotification()
    const [fullName, setFullName] = useState('')
    const [phone, setPhone] = useState('')
    const [email, setEmail] = useState('')
    const [roleId, setRoleId] = useState(2)
    const [isActive, setIsActive] = useState(true)
    const [allowGalleryUpload, setAllowGalleryUpload] = useState(true)
    const [allowCameraCapture, setAllowCameraCapture] = useState(true)
    const [showPhotoGps, setShowPhotoGps] = useState(true)
    const [loading, setLoading] = useState(false)
    const [showCustomerModal, setShowCustomerModal] = useState(false)
    const [showTemplateModal, setShowTemplateModal] = useState(false)
    const [roleOptions, setRoleOptions] = useState([])
    const [files, setFiles] = useState([])

    useEffect(() => {
        if (user) {
            setFullName(user.FullName || user.Name || '')
            setPhone(user.PhoneNo || '')
            setEmail(user.Email || '')
            setRoleId(user.RoleId || 2)
            setIsActive(user.IsActive ?? true)
            // Default to true unless the backend explicitly sends false
            setAllowGalleryUpload(
                typeof user.AllowGalleryUpload === 'boolean'
                    ? user.AllowGalleryUpload
                    : true,
            )
            setAllowCameraCapture(
                typeof user.AllowCameraCapture === 'boolean'
                    ? user.AllowCameraCapture
                    : true,
            )
            // NULL / missing => true (backward compat with always-on default)
            setShowPhotoGps(
                typeof user.ShowPhotoGps === 'boolean'
                    ? user.ShowPhotoGps
                    : true,
            )
            if (user.ImageLocation) {
                setFiles([
                    {
                        id: new Date().getTime().toString(),
                        url: user.ImageLocation,
                        name: 'Profile Image',
                        local: false,
                        SignedURL: user.ImageLocation,
                    },
                ])
            } else {
                setFiles([])
            }
        }
    }, [user, open])

    // UserSetting/ShowPhotoGps is scoped to the caller's own account
    useEffect(() => {
        if (!open || !isProfileEdit) return undefined
        let cancelled = false
        const loadShowPhotoGps = async () => {
            try {
                const res = await ShowPhotoGpsService.getUser()
                if (!cancelled) {
                    setShowPhotoGps(ShowPhotoGpsService.effective(res))
                }
            } catch (error) {
                console.log('ShowPhotoGps user load error:', error)
            }
        }
        loadShowPhotoGps()
        return () => {
            cancelled = true
        }
    }, [open, isProfileEdit])

    useEffect(() => {
        if (open) {
            const fetchRoles = async () => {
                try {
                    // purpose=user: the department role model's roles for opted-in companies, else the usual list
                    const res = await BaseService.get(`${APIPath.roleType}?purpose=user`)
                    if (Array.isArray(res)) {
                        const options = res.map(role => ({
                            key: role.RoleId,
                            value: role.RoleId,
                            text: role.RoleName,
                        }))
                        // If user has a role that's not in the list, add it
                        if (user?.Role && user?.RoleId) {
                            const roleExists = options.some(opt => opt.value === user.RoleId)
                            if (!roleExists) {
                                options.push({
                                    key: user.RoleId,
                                    value: user.RoleId,
                                    text: user.Role,
                                })
                            }
                        }
                        setRoleOptions(options)
                    }
                } catch (e) {
                    // Intentionally ignore role fetch errors
                    // If API fails but user has Role property, use it
                    if (user?.Role && user?.RoleId) {
                        setRoleOptions([{
                            key: user.RoleId,
                            value: user.RoleId,
                            text: user.Role,
                        }])
                    }
                }
            }
            fetchRoles()
        }
    }, [open, user])

    const addMedia = async files => {
        if (!files || files.length === 0) return []
        const allFiles = await Promise.all(
            files.map(async ele => {
                let uploadResponse = ele
                const realFile = ele.file?.file || ele.file
                if (ele?.local) {
                    uploadResponse = await InspectionService.uploadMedia({
                        media: realFile,
                    })
                }
                const result = await InspectionService.saveMedia([
                    {
                        MediaId: ele.MediaId || 0,
                        FAQId: 0, // Not used for user, but required by API
                        Order: 1,
                        FolderName: '1',
                        Comment: uploadResponse?.Comment || '',
                        Caption: uploadResponse?.Caption || ele.Caption || '',
                        FileType:
                            uploadResponse?.FileType || realFile?.type || '',
                        SignedURL:
                            uploadResponse?.ImagePathUrl ||
                            uploadResponse?.FileURL,
                        FileURL:
                            uploadResponse?.ImagePathUrl ||
                            uploadResponse?.FileURL,
                        FileKey:
                            uploadResponse?.CloudImageId ||
                            uploadResponse?.FileKey,
                    },
                ])
                // Always set the preview url to the S3 URL after upload
                return {
                    ...result[0],
                    url: result[0].SignedURL || result[0].FileURL,
                    local: false,
                }
            }),
        )
        setFiles([allFiles[0]])
        return [allFiles[0]]
    }

    const onFileUpload = async uploadedFiles => {
        try {
            // Show instant preview using local object URL
            const localFile = uploadedFiles[0];
            setFiles([{
                ...localFile,
                url: localFile.url, // This is the local object URL from FileUploadPicker
                local: true,
            }]);
            // Now upload and update with S3 URL for persistence
            const uploaded = await addMedia(uploadedFiles);
            // After upload, update with S3 URL for persistence
            setFiles([{
                ...uploaded[0],
                url: uploaded[0].SignedURL || uploaded[0].FileURL, // S3 URL for persistence
                local: false,
            }]);
        } catch (error) {
            console.error('File upload error:', error);
        }
    }

    const onRemoveFile = async () => {
        setFiles([])
    }

    const invitedFlag =
        user?.IsInvited === true ||
        user?.isInvited === true ||
        user?.IsInvitationPending === true ||
        String(user?.Status || user?.InviteStatus || '').toLowerCase() ===
            'invited'

    const handleRemoveInvite = async () => {
        setLoading(true)
        try {
            await UsersService.removeInvitedTeamMember(user.unique_id)
            successToast('Invitation removed')
            setLoading(false)
            onClose(true)
        } catch (e) {
            errorToast('Could not remove this invitation')
            setLoading(false)
        }
    }

    const handleSave = async () => {
        setLoading(true)
        try {
            // For profile edit, use existing RoleId and IsActive values
            // When editing own profile, always send IsActive as true (user is logged in, so must be active)
            const payload = {
                UserId: user.unique_id,
                FullName: fullName,
                PhoneNo: phone,
                RoleId: isProfileEdit ? (user.RoleId || roleId) : roleId,
                IsActive: isProfileEdit ? true : isActive,
                ImageLocation: files[0]?.SignedURL || '',
                UpdatedBy: user.unique_id,
                AllowGalleryUpload: allowGalleryUpload,
                AllowCameraCapture: allowCameraCapture,
                ShowPhotoGps: showPhotoGps,
            }

            // IsActive false in this update is the deactivation (no second call: User/{id}/DeactiveUser only
            // works on the signed-in user's own account and deletes it).
            await BaseService.post(APIPath.userUpdate, payload)
            // Dedicated UserSetting endpoint is scoped to the caller's own account
            if (isProfileEdit) {
                await ShowPhotoGpsService.setUser(showPhotoGps)
            }
            successToast(isProfileEdit ? 'Profile updated successfully' : 'User updated successfully')

            // If onUserUpdate callback is provided, call it with updated user data
            if (onUserUpdate) {
                const updatedUser = {
                    ...user,
                    FullName: fullName,
                    PhoneNo: phone,
                    RoleId: payload.RoleId,
                    IsActive: payload.IsActive,
                    AllowGalleryUpload: allowGalleryUpload,
                    AllowCameraCapture: allowCameraCapture,
                    ShowPhotoGps: showPhotoGps,
                    ImageLocation: files[0]?.SignedURL || user.ImageLocation || '',
                    Image: files[0]?.SignedURL || user.Image || user.ImageLocation || '',
                }
                onUserUpdate(updatedUser)
            }

            setLoading(false)
            onClose(true)
        } catch (e) {
            errorToast(userUpdateErrorMessage(e, 'Failed to update user details'))
            setLoading(false)
        }
    }

    return (
        <Modal open={open} onClose={() => onClose(false)} size="small">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.2rem 1.5rem 0.5rem 1.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontSize: 22, fontWeight: 500 }}>
                        {isProfileEdit ? 'Edit Profile' : 'Edit User'}
                    </div>
                    {isProfileEdit && email && (
                        <div style={{ fontSize: 14, color: '#666' }}>
                            {email}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => onClose(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 28, lineHeight: 1 }}
                    aria-label="Close"
                >
                    <X size={28} />
                </button>
            </div>
            <Modal.Content>
                <Form style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Form.Field>
                        <label>Full Name</label>
                        <Form.Input
                            value={fullName}
                            onChange={e => setFullName(e.target.value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Phone</label>
                        <Form.Input
                            type="tel"
                            value={phone}
                            onKeyPress={(e) => {
                                // Prevent alphabetic characters from being entered
                                const char = String.fromCharCode(e.which)
                                if (!/[0-9+\-() ]/.test(char)) {
                                    e.preventDefault()
                                }
                            }}
                            onChange={e => {
                                // Filter out any alphabetic characters that might have been pasted
                                const filteredValue = e.target.value.replace(/[^0-9+\-() ]/g, '')
                                setPhone(filteredValue)
                            }}
                            placeholder="Enter phone number"
                        />
                    </Form.Field>
                    {!isProfileEdit && (
                        <Form.Field>
                            <label>Email</label>
                            <Form.Input value={email} readOnly />
                        </Form.Field>
                    )}
                    <Form.Field>
                        <label>Role</label>
                        <Dropdown
                            selection
                            options={roleOptions}
                            value={roleId}
                            onChange={(_, data) => setRoleId(data.value)}
                            placeholder={roleOptions.length === 0 ? (user?.Role || 'Loading...') : 'Select Role'}
                        />
                    </Form.Field>
                    {!isProfileEdit && (
                        <Form.Field>
                            <Checkbox
                                label="Active"
                                checked={isActive}
                                onChange={(_, data) => setIsActive(data.checked)}
                            />
                        </Form.Field>
                    )}
                    <Form.Field>
                        <Checkbox
                            label="Enable photo selection from device gallery"
                            checked={allowGalleryUpload}
                            onChange={(_, data) =>
                                setAllowGalleryUpload(Boolean(data.checked))
                            }
                        />
                    </Form.Field>
                    <Form.Field>
                        <Checkbox
                            label="Enable capturing photos with device camera"
                            checked={allowCameraCapture}
                            onChange={(_, data) =>
                                setAllowCameraCapture(Boolean(data.checked))
                            }
                        />
                    </Form.Field>
                    <Form.Field>
                        <Checkbox
                            toggle
                            label="Show GPS Caption in PDF"
                            checked={showPhotoGps}
                            onChange={(_, data) =>
                                setShowPhotoGps(Boolean(data.checked))
                            }
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Profile Image</label>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <FileUploadPicker
                                maxFiles={1}
                                label={null}
                                maxFileSize={500}
                                accept={AcceptFileType.all}
                                addedNewFiles={onFileUpload}
                                onRemove={onRemoveFile}
                                files={files}
                                remove
                                noPreview={false}
                                previewItem={<CustomePreview className="media-item" />}
                                previewItemClassName="media-item"
                            />
                        </div>
                    </Form.Field>
                </Form>
                {!isProfileEdit && (
                    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                        <AppPrimaryButton
                            type="button"
                            onClick={() => setShowCustomerModal(true)}
                            className="mr-2"
                        >
                            Assign Customer
                        </AppPrimaryButton>
                        <AppPrimaryButton
                            type="button"
                            onClick={() => setShowTemplateModal(true)}
                        >
                            Assign Template
                        </AppPrimaryButton>
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {invitedFlag && !isProfileEdit && (
                    <AppSecondaryButton
                        type="button"
                        onClick={handleRemoveInvite}
                        disabled={loading}
                    >
                        Remove invitation
                    </AppSecondaryButton>
                )}
                <AppSecondaryButton onClick={() => onClose(false)} disabled={loading}>
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton onClick={handleSave} loading={loading} disabled={loading}>
                    Save
                </AppPrimaryButton>
            </Modal.Actions>
            <CustomerAssignModal
                open={showCustomerModal}
                onClose={() => setShowCustomerModal(false)}
                userId={user?.unique_id}
            />
            <TemplateAssignModal
                open={showTemplateModal}
                onClose={() => setShowTemplateModal(false)}
                userId={user?.unique_id}
            />
        </Modal>
    )
}
